
import React, { useEffect, useState, useCallback } from 'react';
import DocumentHeader from '../../../../documents/components/DocumentHeader';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TablePagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Card,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import CommonAccountTable from '../../../../documents/components/CommonAccountTable';
import { AccountTableColumn } from '../../../../types';
import { request } from '../../../../../../utils/request';
import { useDebounce } from '../../../../../../utils/useDebounce';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteConfirmDialog from '../../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { usePermissions } from '../../../../../../utils/usePermissions';

const ListQuotation = () => {
  const { canDeleteQuotation } = usePermissions();
  const [loadingGst, setLoadingGst] = useState(true);
  const [loadingNonGst, setLoadingNonGst] = useState(true);
  const [gstQuotations, setGstQuotations] = useState<any[]>([]);
  const [nonGstQuotations, setNonGstQuotations] = useState<any[]>([]);

  // Pagination state for GST
  const [gstPage, setGstPage] = useState(0);
  const [gstRowsPerPage, setGstRowsPerPage] = useState(10);
  const [gstTotal, setGstTotal] = useState(0);

  // Pagination state for NON-GST
  const [nonGstPage, setNonGstPage] = useState(0);
  const [nonGstRowsPerPage, setNonGstRowsPerPage] = useState(10);
  const [nonGstTotal, setNonGstTotal] = useState(0);

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  // Filters state
  const [searchGst, setSearchGst] = useState('');
  const [searchNonGst, setSearchNonGst] = useState('');
  const [statusFilterGst, setStatusFilterGst] = useState<string>('all');
  const [statusFilterNonGst, setStatusFilterNonGst] = useState<string>('all');
  const [dateRangeGst, setDateRangeGst] = useState<string>('all');
  const [dateRangeNonGst, setDateRangeNonGst] = useState<string>('all');
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [customDateDialogFor, setCustomDateDialogFor] = useState<'gst' | 'nonGst'>('gst');

  const navigate = useNavigate();

  const { gstEnabled } = useGstMode();

  const statusFilterOptions = [
    { value: 'all', label: 'All Quotations' },
    { value: 'OPEN', label: 'Open Quotation' },
    { value: 'CLOSED', label: 'Closed Quotation' },
    { value: 'EXPIRED', label: 'Expired Quotation' },
    { value: 'CONVERTED', label: 'Converted Quotation' },
  ]

  // Debounced searches
  const debouncedSearchGst = useDebounce(searchGst, 500);
  const debouncedSearchNonGst = useDebounce(searchNonGst, 500);

  // Fetch GST quotations
  const fetchGstQuotations = useCallback(async (isForCards = false) => {
    if (!isForCards) setLoadingGst(true);

    try {
      const params: Record<string, any> = {
        page: isForCards ? 1 : gstPage + 1,
        limit: isForCards ? 1000 : gstRowsPerPage,
        gstType: 'GST'
      };

      if (debouncedSearchGst) params.search = debouncedSearchGst;
      if (statusFilterGst && statusFilterGst !== 'all') params.status = statusFilterGst;

      // Handle date range
      if (dateRangeGst === 'custom' && startDate && endDate && customDateDialogFor === 'gst') {
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = endDate.toISOString().split('T')[0];
      } else if (dateRangeGst && dateRangeGst !== 'all' && dateRangeGst !== 'custom') {
        params.dateRange = dateRangeGst;
      }

      const res = await request({
        url: 'quotation/get-all-quotations',
        method: 'get',
        params
      });

      if (res.success && res.data) {
        if (isForCards) {
          return res.data;
        } else {
          setGstQuotations(res.data);
          setGstTotal(res.pagination?.total || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching GST quotations:', error);
      if (!isForCards) {
        toast.error('Failed to fetch GST quotations');
      }
    } finally {
      if (!isForCards) setLoadingGst(false);
    }
    return [];
  }, [gstPage, gstRowsPerPage, debouncedSearchGst, statusFilterGst, dateRangeGst, startDate, endDate, customDateDialogFor]);

  // Fetch NON-GST quotations
  const fetchNonGstQuotations = useCallback(async (isForCards = false) => {
    if (!gstEnabled) {
      if (!isForCards) setLoadingNonGst(true);

      try {
        const params: Record<string, any> = {
          page: isForCards ? 1 : nonGstPage + 1,
          limit: isForCards ? 1000 : nonGstRowsPerPage,
          gstType: 'NON-GST'
        };

        if (debouncedSearchNonGst) params.search = debouncedSearchNonGst;
        if (statusFilterNonGst && statusFilterNonGst !== 'all') params.status = statusFilterNonGst;

        // Handle date range
        if (dateRangeNonGst === 'custom' && startDate && endDate && customDateDialogFor === 'nonGst') {
          params.startDate = startDate.toISOString().split('T')[0];
          params.endDate = endDate.toISOString().split('T')[0];
        } else if (dateRangeNonGst && dateRangeNonGst !== 'all' && dateRangeNonGst !== 'custom') {
          params.dateRange = dateRangeNonGst;
        }

        const res = await request({
          url: 'quotation/get-all-quotations',
          method: 'get',
          params
        });

        if (res.success && res.data) {
          if (isForCards) {
            return res.data;
          } else {
            setNonGstQuotations(res.data);
            setNonGstTotal(res.pagination?.total || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching NON-GST quotations:', error);
        if (!isForCards) {
          toast.error('Failed to fetch NON-GST quotations');
        }
      } finally {
        if (!isForCards) setLoadingNonGst(false);
      }
    }
    return [];
  }, [nonGstPage, nonGstRowsPerPage, debouncedSearchNonGst, statusFilterNonGst, dateRangeNonGst, startDate, endDate, customDateDialogFor, gstEnabled]);


  // Main data fetch for tables
  useEffect(() => {
    fetchGstQuotations(false);
    if (!gstEnabled) {
      fetchNonGstQuotations(false);
    }
  }, [fetchGstQuotations, fetchNonGstQuotations, gstEnabled]);

  // Handle page change for GST
  const handleGstPageChange = (event: unknown, newPage: number) => {
    setGstPage(newPage);
  };

  // Handle rows per page change for GST
  const handleGstRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setGstRowsPerPage(newRowsPerPage);
    setGstPage(0);
  };

  // Handle page change for NON-GST
  const handleNonGstPageChange = (event: unknown, newPage: number) => {
    setNonGstPage(newPage);
  };

  // Handle rows per page change for NON-GST
  const handleNonGstRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setNonGstRowsPerPage(newRowsPerPage);
    setNonGstPage(0);
  };

  // Handle search for GST
  const handleGstSearch = (value: string) => {
    setSearchGst(value);
    setGstPage(0);
  };

  // Handle search for NON-GST
  const handleNonGstSearch = (value: string) => {
    setSearchNonGst(value);
    setNonGstPage(0);
  };

  // Handle date range change for GST
  const handleGstDateRangeChange = (range: string) => {
    if (range === 'custom') {
      setCustomDateDialogFor('gst');
      setCustomDateDialog(true);
    } else {
      setDateRangeGst(range);
      if (range !== 'custom') {
        setStartDate(null);
        setEndDate(null);
      }
      setGstPage(0);
    }
  };

  // Handle date range change for NON-GST
  const handleNonGstDateRangeChange = (range: string) => {
    if (range === 'custom') {
      setCustomDateDialogFor('nonGst');
      setCustomDateDialog(true);
    } else {
      setDateRangeNonGst(range);
      if (range !== 'custom') {
        setStartDate(null);
        setEndDate(null);
      }
      setNonGstPage(0);
    }
  };

  // Handle custom date range click
  const handleCustomDateClick = (type: 'gst' | 'nonGst') => {
    setCustomDateDialogFor(type);
    setCustomDateDialog(true);
  };

  // Handle custom date apply
  const handleApplyCustomDates = () => {
    if (startDate && endDate) {
      if (customDateDialogFor === 'gst') {
        setDateRangeGst('custom');
        setGstPage(0);
      } else {
        setDateRangeNonGst('custom');
        setNonGstPage(0);
      }
      setCustomDateDialog(false);
    } else {
      toast.warning('Please select both start and end dates');
    }
  };

  // Handle status filter change for GST
  const handleGstStatusFilterChange = (status: string | string[]) => {
    const value = Array.isArray(status) ? status[0] : status;
    setStatusFilterGst(value === 'all' ? 'all' : value);
    setGstPage(0);
  };

  // Handle status filter change for NON-GST
  const handleNonGstStatusFilterChange = (status: string | string[]) => {
    const value = Array.isArray(status) ? status[0] : status;
    setStatusFilterNonGst(value === 'all' ? 'all' : value);
    setNonGstPage(0);
  };

  const CustomDatePicker = ({ label, value, onChange, minDate }: any) => (
    <DatePicker
      label={label}
      value={value}
      onChange={(newValue: any) => {
        onChange(newValue as Date | null);
      }}
      slotProps={{
        textField: {
          fullWidth: true,
          size: "small"
        }
      }}
      minDate={minDate}
    />
  );

  // Custom date range dialog with date-fns
  const renderCustomDateDialog = () => (
    <Dialog
      open={customDateDialog}
      onClose={() => {
        setCustomDateDialog(false);
        // If no dates are selected, reset to all dates
        if (!startDate || !endDate) {
          if (customDateDialogFor === 'gst') {
            setDateRangeGst('all');
          } else {
            setDateRangeNonGst('all');
          }
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Select Custom Date Range for {customDateDialogFor === 'gst' ? 'GST' : 'Non-GST'} quotations
        <IconButton
          aria-label="close"
          onClick={() => {
            setCustomDateDialog(false);
            // If no dates are selected, reset to all dates
            if (!startDate || !endDate) {
              if (customDateDialogFor === 'gst') {
                setDateRangeGst('all');
              } else {
                setDateRangeNonGst('all');
              }
            }
          }}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: 1,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <Box sx={{ flex: 1 }}>
              <CustomDatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomDatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate || undefined}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setCustomDateDialog(false);
          if (customDateDialogFor === 'gst') {
            setDateRangeGst('all');
          } else {
            setDateRangeNonGst('all');
          }
          setStartDate(null);
          setEndDate(null);
        }}>
          Cancel
        </Button>
        <Button
          onClick={handleApplyCustomDates}
          variant="contained"
          disabled={!startDate || !endDate}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Delete handlers
  const handleDeleteClick = (quotation: any) => {
    setItemToDelete(quotation);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoadingDelete(true);
    try {
      const res = await request({
        url: `quotation/delete-quotation/${itemToDelete._id}`,
        method: 'delete',
      });

      if (res.success) {
        setOpenDeleteDialog(false);
        toast.success(res.message || 'Quotation deleted successfully');
        // Refresh both tables
        await Promise.all([
          fetchGstQuotations(false),
          !gstEnabled && fetchNonGstQuotations(false)
        ]);
        setItemToDelete(null);
      } else {
        toast.error(res.message || 'Failed to delete quotation');
      }
    } catch (error: any) {
      console.error('Error deleting quotation:', error);
      toast.error(error || 'Failed to delete quotation');
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  };

  // Columns definition
  const columns: AccountTableColumn[] = [
    {
      id: 'quotationDate',
      label: 'DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.quotationDate) return 'N/A';
        try {
          return new Date(row.quotationDate).toLocaleDateString('en-GB');
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    { id: 'quotationNumber', label: 'QUOTATION NUMBER', minWidth: 150 },
    {
      id: 'party',
      label: 'PARTY NAME',
      minWidth: 200,
      renderCell: (row) => {
        if (row.party && typeof row.party === 'object') {
          return row.party.partyName || row.party.nickName || 'N/A';
        }
        return 'N/A';
      }
    },
    {
      id: 'dueDate',
      label: 'DUE DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.dueDate) return 'N/A';
        try {
          const dueDate = new Date(row.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);

          const diffTime = dueDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays > 0) return `${diffDays} Days`;
          if (diffDays === 0) return 'Today';
          return `${Math.abs(diffDays)} Days Ago`;
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    {
      id: 'totalAmount',
      label: 'AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.totalAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'status',
      label: 'STATUS',
      minWidth: 120,
      renderCell: (row) => {
        const status = row.status || 'OPEN';
        const statusColors: Record<string, 'success' | 'warning' | 'primary' | 'error'> = {
          OPEN: 'success',
          CLOSED: 'warning',
          EXPIRED: 'error',
          CONVERTED: 'primary'
        };

        return (
          <Chip
            label={status}
            color={statusColors[status as keyof typeof statusColors] || 'default'}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      minWidth: 120,
      align: 'center',
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 0.8 }}>
          {row.status === 'CONVERTED' ? "" : (<Button
            variant="text"
            fullWidth
            onClick={() => navigate(`/accounts/sales/quotation/updateQuotation/${row._id}`)}
            sx={{
              minWidth: 'auto',
              fontSize: '0.60rem',
              textWrap: 'nowrap',
              py: 0.1,
              px: 2,
              borderRadius: 5,
              border: '.8px solid #7f5ee666',
              '&:hover': { backgroundColor: '#7f5ee666' }
            }}
          >
            Edit
          </Button>)}

          {canDeleteQuotation() && (
            <Button
              size="small"
              fullWidth
              variant="text"
              color="error"
              onClick={() => handleDeleteClick(row)}
              sx={{
                minWidth: 'auto',
                fontSize: '0.60rem',
                py: 0.1,
                px: 2,
                borderRadius: 5,
                border: '.8px solid #ff4d6e5e',
                '&:hover': { backgroundColor: '#ff4d6e5e' }
              }}
            >
              Delete
            </Button>
          )}

        </Box>
      )
    }
  ];

  if (loadingGst && gstQuotations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader heading='Quotation' />

      {/* GST quotations Section */}
      <Box sx={{ mb: 0 }}>
        <Card sx={{ mb: 2, p: 1 }}>
          <Typography variant="h6" color="primary" >
            GST Quotations
            <Chip
              label={`${gstTotal}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          </Typography>
        </Card>


        {/* GST Table */}
        <CommonAccountTable
          columns={columns}
          data={gstQuotations}
          loading={loadingGst}
          searchPlaceholder="Search GST quotations..."
          searchValue={searchGst}
          onSearch={handleGstSearch}
          showSearchBar={true}
          showDateRange={true}
          selectedDateRange={dateRangeGst}
          onDateRangeChange={handleGstDateRangeChange}
          startDate={customDateDialogFor === 'gst' ? startDate : null}
          endDate={customDateDialogFor === 'gst' ? endDate : null}
          onCustomDateClick={() => handleCustomDateClick('gst')}
          showFilter={true}
          filterOptions={statusFilterOptions}
          selectedFilter={statusFilterGst === 'all' ? 'all' : statusFilterGst}
          onFilterChange={handleGstStatusFilterChange}
          filterLabel="Status"
          createButtonProps={{
            label: 'Create GST Quotation',
            navigateTo: '/accounts/sales/quotation/createQuotation?gstType=GST'
          }}
          idField='_id'
          getRowLink={(row: any) => `/accounts/sales/quotation/${row?._id}`}
        />

        {/* GST Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={gstTotal}
          rowsPerPage={gstRowsPerPage}
          page={gstPage}
          onPageChange={handleGstPageChange}
          onRowsPerPageChange={handleGstRowsPerPageChange}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': {
              minHeight: 52,
              padding: 0,
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
            }
          }}
        />
      </Box>

      {/* NON-GST quotations Section (only when not in GST-only mode) */}
      {!gstEnabled && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Card sx={{ mb: 2, p: 1 }}>
              <Typography variant="h6" color="secondary.contrastText" >
                Non-GST Quotations
                <Chip
                  label={`${nonGstTotal} `}
                  size="small"

                  variant="outlined"
                  sx={{ ml: 1, fontSize: '0.7rem', color: "secondary.contrastText" }}
                />
              </Typography>
            </Card>

            {/* NON-GST Table */}
            <CommonAccountTable
              columns={columns}
              data={nonGstQuotations}
              loading={loadingNonGst}
              searchPlaceholder="Search Non-GST quotations..."
              searchValue={searchNonGst}
              onSearch={handleNonGstSearch}
              showSearchBar={true}
              showDateRange={true}
              selectedDateRange={dateRangeNonGst}
              onDateRangeChange={handleNonGstDateRangeChange}
              startDate={customDateDialogFor === 'nonGst' ? startDate : null}
              endDate={customDateDialogFor === 'nonGst' ? endDate : null}
              onCustomDateClick={() => handleCustomDateClick('nonGst')}
              showFilter={true}
              filterOptions={statusFilterOptions}
              selectedFilter={statusFilterNonGst === 'all' ? 'all' : statusFilterNonGst}
              onFilterChange={handleNonGstStatusFilterChange}
              filterLabel="Status"
              createButtonProps={{
                label: 'Create NON-GST Quotation',
                navigateTo: '/accounts/sales/quotation/createQuotation?gstType=NON-GST'
              }}
              idField='_id'
              getRowLink={(row: any) => `/accounts/sales/quotation/${row?._id}`}
            />

            {/* NON-GST Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={nonGstTotal}
              rowsPerPage={nonGstRowsPerPage}
              page={nonGstPage}
              onPageChange={handleNonGstPageChange}
              onRowsPerPageChange={handleNonGstRowsPerPageChange}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '.MuiTablePagination-toolbar': {
                  minHeight: 52,
                  padding: 0,
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                }
              }}
            />
          </Box>
        </>
      )}

      {/* Custom Date Dialog */}
      {renderCustomDateDialog()}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        loading={loadingDelete}
        title="Delete quotation"
        description={
          <>
            Are you sure you want to delete the quotation{' '}
            <strong>"{itemToDelete?.quotationNumber} - {(itemToDelete?.party && typeof itemToDelete?.party === 'object') ? itemToDelete?.party?.partyName : 'N/A'}"</strong>?
            <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
              This action cannot be undone. All associated data will be permanently removed.
            </Alert>
          </>
        }
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ListQuotation;